import { useState, useEffect, useCallback } from 'react'

import I18n from 'Pods/Shared/I18n'

import { mySalesService } from '../Services/mySalesService'
import { EARNINGS_DETAIL_TYPES } from '../Static/earningsDetailConfig'

export const DEFAULT_PER_PAGE = 40

const INITIAL_META = {
  page: 1, totalPages: 1, totalCount: 0, perPage: DEFAULT_PER_PAGE,
}

const SERVICE_CONFIG = {
  [EARNINGS_DETAIL_TYPES.sales]: {
    fetch: (params) => mySalesService.getLatestSales(params),
    report: (period) => mySalesService.generateSalesReport(period),
    errorMessageKey: 'profile.my_sales.earnings_detail.errors.sales_load_failed',
  },
  [EARNINGS_DETAIL_TYPES.downloads]: {
    fetch: (params) => mySalesService.getLatestDownloads(params),
    report: (period) => mySalesService.generateReport(period),
    errorMessageKey: 'profile.my_sales.earnings_detail.errors.downloads_load_failed',
  },
  [EARNINGS_DETAIL_TYPES.subscriptionEarnings]: {
    fetch: (params) => mySalesService.getSubscriptionSales(params),
    report: (period) => mySalesService.generateSubscriptionEarningsReport(period),
    errorMessageKey: 'profile.my_sales.earnings_detail.errors.subscription_earnings_load_failed',
  },
}

function useEarningsDetail({
  type,
  page = 1,
  perPage = DEFAULT_PER_PAGE,
  month = null,
  enabled = true,
} = {}) {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(INITIAL_META)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState(false)
  const [isReportRequired, setIsReportRequired] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return undefined
    }

    let isMounted = true
    setIsLoading(true)
    setError(false)

    const fetchData = async () => {
      try {
        const response = await SERVICE_CONFIG[type].fetch({ page, perPage, month })
        if (!isMounted) return
        setRows(response.data || [])
        setMeta({ ...INITIAL_META, ...(response.meta || {}) })
      } catch {
        if (isMounted) {
          setRows([])
          setMeta(INITIAL_META)
          setError(true)
          App.events.publish(
            'app:notification:flash:error',
            I18n.t(SERVICE_CONFIG[type].errorMessageKey, { ns: 'rails' })
          )
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    fetchData()

    return () => { isMounted = false }
  }, [type, page, perPage, month, enabled])

  useEffect(() => { setIsReportRequired(false) }, [month])

  const generateReport = useCallback(async () => {
    const status = await SERVICE_CONFIG[type].report(month)
    if (status === 204 || status === 200) {
      setIsReportRequired(true)
    }
  }, [type, month])

  return {
    rows, meta, isLoading, error, generateReport, isReportRequired,
  }
}

export default useEarningsDetail
